import { computeDayRoute, HOTEL_ROUTE_ID, type DayRouteOptions } from "./day-route";
import { openEditDialog } from "./edit-dialog";
import { i18n } from "./i18n";
import { hideDayRoute, showDayRoute } from "./map";
import { stops } from "./stops";
import { formatDuration } from "./travel-time";
import type { DayRoute, Stop } from "./types";

let panelEl: HTMLElement | null = null;
let titleEl: HTMLElement | null = null;
let bodyEl: HTMLElement | null = null;
let recalcBtn: HTMLButtonElement | null = null;
let exportGpxBtn: HTMLButtonElement | null = null;
let currentStopId: string | null = null;
let currentOptions: DayRouteOptions = {};
let pendingAbort: AbortController | null = null;
let currentRoute: DayRoute | null = null;
let currentStop: Stop | null = null;

export function initDayRoutePanel(): void {
  panelEl = required<HTMLElement>("#day-route-panel");
  titleEl = required<HTMLElement>("#day-route-title");
  bodyEl = required<HTMLElement>("#day-route-body");
  recalcBtn = required<HTMLButtonElement>("#day-route-recalculate");
  exportGpxBtn = required<HTMLButtonElement>("#day-route-export-gpx");

  required<HTMLButtonElement>("#day-route-close").addEventListener("click", goBack);
  recalcBtn.addEventListener("click", () => {
    const stop = currentStopId ? stops.get(currentStopId) : null;
    if (stop) startCompute(stop, currentOptions);
  });
  exportGpxBtn.addEventListener("click", () => exportGpx());

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && panelEl?.classList.contains("open")) closePanel();
  });
}

export function openDayRoutePanel(stop: Stop, options: DayRouteOptions = {}): void {
  currentStopId = stop.id;
  currentOptions = options;
  if (titleEl) titleEl.textContent = stop.city || stop.name;
  panelEl?.classList.add("open");
  startCompute(stop, options);
}

export function closeDayRoutePanel(): void {
  closePanel();
}

function startCompute(stop: Stop, options: DayRouteOptions = {}): void {
  pendingAbort?.abort();
  pendingAbort = new AbortController();
  showLoading();

  computeDayRoute(stop, pendingAbort.signal, options)
    .then((route) => {
      if (!panelEl?.classList.contains("open") || currentStopId !== stop.id) return;
      stops.update(stop.id, { dayRoute: route });
      const updated = stops.get(stop.id) ?? stop;
      renderRoute(updated, route);
      showDayRoute(updated, route);
    })
    .catch((err: unknown) => {
      if ((err as Error).name === "AbortError") return;
      if (!panelEl?.classList.contains("open")) return;
      const msg =
        (err as Error).message === "need_more_places"
          ? i18n.t("dayRouteNoPlaces")
          : i18n.t("dayRouteFailed");
      showError(msg);
    });
}

function showLoading(): void {
  if (!bodyEl) return;
  const p = document.createElement("p");
  p.className = "day-route-status";
  p.textContent = i18n.t("dayRouteLoading");
  bodyEl.replaceChildren(p);
  if (recalcBtn) recalcBtn.disabled = true;
  if (exportGpxBtn) exportGpxBtn.hidden = true;
}

function showError(msg: string): void {
  if (!bodyEl) return;
  const p = document.createElement("p");
  p.className = "day-route-status day-route-status--error";
  p.textContent = msg;
  bodyEl.replaceChildren(p);
  if (recalcBtn) recalcBtn.disabled = false;
  if (exportGpxBtn) exportGpxBtn.hidden = true;
}

function renderRoute(stop: Stop, route: DayRoute): void {
  if (!bodyEl) return;

  const placeMap = new Map((stop.places ?? []).map((p) => [p.id, p]));
  const frag = document.createDocumentFragment();
  const isRoundtrip = !!route.roundtripHotel;

  if (isRoundtrip) {
    frag.appendChild(makeHotelCard(route.roundtripHotel!.name));
    const firstLeg = route.legs[0];
    if (firstLeg) frag.appendChild(makeLegEl(firstLeg));
  }

  route.orderedPlaceIds.forEach((id, idx) => {
    const place = placeMap.get(id);
    if (!place) return;

    const card = document.createElement("div");
    card.className = "day-route-place";

    const num = document.createElement("span");
    num.className = "day-route-num";
    num.textContent = String(idx + 1);

    const name = document.createElement("span");
    name.className = "day-route-place-name";
    name.textContent = place.name;
    name.title = place.name;

    card.append(num, name);
    frag.appendChild(card);

    // For roundtrip: legs[0] is hotel→place1, place-to-place legs start at index 1
    // For open route: legs[idx] is the leg after place idx
    const legIdx = isRoundtrip ? idx + 1 : idx;
    const leg = route.legs[legIdx];
    if (leg) frag.appendChild(makeLegEl(leg));
  });

  if (isRoundtrip) {
    frag.appendChild(makeHotelCard(route.roundtripHotel!.name));
  }

  const totalMin = route.legs.reduce((s, l) => s + l.durationMin, 0);
  const totalKm = route.legs.reduce((s, l) => s + l.distanceKm, 0);
  const totalEl = document.createElement("div");
  totalEl.className = "day-route-total";
  totalEl.textContent = `${i18n.t("dayRouteTotalTime", { t: formatDuration(totalMin) })} · ${totalKm.toFixed(1)} km`;
  frag.appendChild(totalEl);

  bodyEl.replaceChildren(frag);
  if (recalcBtn) recalcBtn.disabled = false;
  if (exportGpxBtn) exportGpxBtn.hidden = false;
  currentRoute = route;
  currentStop = stop;
}

function makeHotelCard(name: string): HTMLElement {
  const card = document.createElement("div");
  card.className = "day-route-place day-route-place--hotel";
  const dot = document.createElement("span");
  dot.className = "day-route-num day-route-num--hotel";
  dot.textContent = "H";
  const nameEl = document.createElement("span");
  nameEl.className = "day-route-place-name";
  nameEl.textContent = name;
  nameEl.title = name;
  card.append(dot, nameEl);
  return card;
}

function makeLegEl(leg: DayRoute["legs"][number]): HTMLElement {
  const legEl = document.createElement("div");
  legEl.className = `day-route-leg day-route-leg--${leg.mode}`;

  const modeBadge = document.createElement("span");
  modeBadge.className = "day-route-leg-mode";
  modeBadge.textContent =
    leg.mode === "walk" ? i18n.t("dayRouteWalk") : i18n.t("dayRouteTransit");

  const infoSpan = document.createElement("span");
  infoSpan.className = "day-route-leg-info";
  const dur = formatDuration(leg.durationMin);
  const km = leg.distanceKm.toFixed(1);
  infoSpan.textContent =
    leg.mode === "transit"
      ? `${km} km · ${dur} (${i18n.t("dayRouteEstimated")})`
      : `${km} km · ${dur}`;

  legEl.append(modeBadge, infoSpan);
  return legEl;
}

function closePanel(): void {
  pendingAbort?.abort();
  pendingAbort = null;
  panelEl?.classList.remove("open");
  hideDayRoute();
  currentStopId = null;
  currentOptions = {};
  currentRoute = null;
  currentStop = null;
}

function goBack(): void {
  const stop = currentStopId ? stops.get(currentStopId) : null;
  closePanel();
  if (stop) openEditDialog(stop, { initialTab: "places" });
}

function escapeXml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function exportGpx(): void {
  if (!currentRoute || !currentStop) return;

  const placeMap = new Map((currentStop.places ?? []).map((p) => [p.id, p]));
  const cityName = currentStop.city || currentStop.name;
  const isRoundtrip = !!currentRoute.roundtripHotel;

  // Build waypoints: hotel first (if roundtrip), then ordered places
  const wpts: string[] = [];
  if (isRoundtrip && currentStop.hotel?.lat != null && currentStop.hotel.lon != null) {
    wpts.push(
      `  <wpt lat="${currentStop.hotel.lat}" lon="${currentStop.hotel.lon}"><name>${escapeXml(currentRoute.roundtripHotel!.name)}</name></wpt>`,
    );
  }
  for (const id of currentRoute.orderedPlaceIds) {
    if (id === HOTEL_ROUTE_ID) continue;
    const place = placeMap.get(id);
    if (!place || place.lat == null || place.lon == null) continue;
    wpts.push(
      `  <wpt lat="${place.lat}" lon="${place.lon}"><name>${escapeXml(place.name)}</name></wpt>`,
    );
  }
  if (isRoundtrip && currentStop.hotel?.lat != null && currentStop.hotel.lon != null) {
    wpts.push(
      `  <wpt lat="${currentStop.hotel.lat}" lon="${currentStop.hotel.lon}"><name>${escapeXml(currentRoute.roundtripHotel!.name)}</name></wpt>`,
    );
  }

  // Build track from all leg polylines concatenated
  const trkpts = currentRoute.legs
    .flatMap((leg) => leg.polyline ?? [])
    .map(([lat, lon]) => `      <trkpt lat="${lat}" lon="${lon}"/>`)
    .join("\n");

  const gpx = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="TripPlanner" xmlns="http://www.topografix.com/GPX/1/1">
  <metadata><name>${escapeXml(cityName)}</name></metadata>
${wpts.join("\n")}
  <trk>
    <name>${escapeXml(cityName)} Day Route</name>
    <trkseg>
${trkpts}
    </trkseg>
  </trk>
</gpx>`;

  const filename = `${cityName.replace(/[/\\:*?"<>|]/g, "-")}-route.gpx`;
  const blob = new Blob([gpx], { type: "application/gpx+xml" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function required<T extends Element>(selector: string): T {
  const el = document.querySelector<T>(selector);
  if (!el) throw new Error(`Element not found: ${selector}`);
  return el;
}
